"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const events_1 = __importDefault(require("events"));
const rxjs_1 = require("rxjs");
const queryString_service_1 = __importDefault(require("./services/queryString.service"));
const storage_service_1 = __importDefault(require("./services/storage.service"));
const AuthUtils_1 = __importDefault(require("./utils/AuthUtils"));
class TokenValidator {
    constructor(config) {
        this.config = config;
        this.DEFAULT_TOKEN_KEY = 'auth0client';
        this.INVALID_TOKEN_EVENT = 'onInvalidTokenEvent';
        this.DEFAULT_REDIRECT_LOGOUT_URL = 'https://online.naranjax.com/login';
        this.storageService = storage_service_1.default.fromInstance();
        this.queryStringService = queryString_service_1.default.fromInstance();
        this.refreshTokenSubscription = null;
        this.requestNewTokenSubscription = null;
        this.eventEmitter = new events_1.default();
        this.tokenBehaviorSubject = new rxjs_1.BehaviorSubject(null);
        this.tokenObservable = this.tokenBehaviorSubject.asObservable();
        this.tokenKey = this.config.tokenKey ?? this.DEFAULT_TOKEN_KEY;
        this.userProfileBehaviorSubject = new rxjs_1.BehaviorSubject(null);
        this.userProfileObservable = this.userProfileBehaviorSubject
            .asObservable()
            .pipe((0, rxjs_1.filter)((user) => !!user), (0, rxjs_1.catchError)((err) => {
            return err;
        }));
        this.isAuthenticatedBehaviorSubject = new rxjs_1.BehaviorSubject(null);
        this.isAuthenticatedObservable =
            this.isAuthenticatedBehaviorSubject.asObservable();
        this.requestNewTokenBehaviorSubject = new rxjs_1.BehaviorSubject(false);
        this.requestNewTokenObservable =
            this.requestNewTokenBehaviorSubject.asObservable();
        this.handleTokenBehavior();
    }
    handleTokenBehavior() {
        const token = this.storageService.get(this.tokenKey);
        if (token && this.isValidToken(token)) {
            this.processValidToken(token);
            if (this.config.renewTokenEnabled) {
                this.renewToken(token);
            }
        }
        else {
            this.processInvalidToken();
        }
    }
    processValidToken(token) {
        this.updateToken(token);
        this.userProfileBehaviorSubject.next(AuthUtils_1.default.parseToken(token));
        this.isAuthenticatedBehaviorSubject.next(!AuthUtils_1.default.isTokenExpired(token));
    }
    processInvalidToken() {
        this.storageService.clear(this.tokenKey);
        this.userProfileBehaviorSubject.next(null);
        this.isAuthenticatedBehaviorSubject.next(false);
        const logoutUrl = this.config?.logoutUrl ?? this.DEFAULT_REDIRECT_LOGOUT_URL;
        this.eventEmitter.emit(this.INVALID_TOKEN_EVENT, logoutUrl);
    }
    getTokenObservable() {
        return this.tokenObservable;
    }
    getToken() {
        return this.storageService.get(this.tokenKey);
    }
    getUserProfileObservable() {
        return this.userProfileObservable;
    }
    getUserProfile() {
        return this.userProfileBehaviorSubject.getValue();
    }
    getIsAuthenticatedObservable() {
        return this.isAuthenticatedObservable;
    }
    getIsAuthenticated() {
        return this.isAuthenticatedBehaviorSubject.getValue();
    }
    getRequestNewTokenObservable() {
        return this.requestNewTokenObservable;
    }
    getRequestNewToken() {
        return this.requestNewTokenBehaviorSubject.getValue();
    }
    hasUserRolePermission(allowedRoles) {
        const token = this.getToken();
        if (!token) {
            return false;
        }
        if (allowedRoles.includes(AuthUtils_1.default.getUserRoleFromToken(token))) {
            return true;
        }
        return false;
    }
    isValidToken(token) {
        if (!AuthUtils_1.default.hasExpirationTimeProperty(token)) {
            return false;
        }
        if (AuthUtils_1.default.isTokenExpired(token)) {
            return false;
        }
        return AuthUtils_1.default.decode(token) ? true : false;
    }
    updateToken(token) {
        this.storageService.set(this.tokenKey, token);
        this.tokenBehaviorSubject.next(token);
    }
    renewToken(token) {
        this.refreshTokenSubscription?.unsubscribe();
        this.requestNewTokenSubscription?.unsubscribe();
        this.requestNewTokenSubscription = AuthUtils_1.default.timerRenewalForRefreshToken(token, this.config.isDebugEnabled ?? false, this.config.timeBeforeRefreshToken)
            .pipe((0, rxjs_1.filter)((value) => value !== null))
            .subscribe(() => {
            this.requestNewTokenBehaviorSubject.next(true);
        });
        this.refreshTokenSubscription = AuthUtils_1.default.timerRenewal(token, this.config.isDebugEnabled ?? false)
            .pipe((0, rxjs_1.filter)((value) => value !== null))
            .subscribe(() => {
            this.handleRefreshTokenSubscription();
        });
    }
    handleRefreshTokenSubscription() {
        this.queryStringService.pushToken(null);
        this.isAuthenticatedBehaviorSubject.next(false);
        this.refreshTokenSubscription = this.queryStringService
            .getRefreshTokenObservable()
            .subscribe((token) => {
            if (token) {
                if (this.refreshTokenSubscription) {
                    this.refreshTokenSubscription.unsubscribe();
                }
                this.updateToken(token);
                this.renewToken(token);
            }
        });
    }
}
exports.default = TokenValidator;
